import type { TFunction } from "i18next"

import type { ToastProps } from "@f2e/ui/components/base-legacy/use-toast"
import { SUPPORTED_ATTACHMENT_MIME_TYPES } from "@f2e/shared/constants"

import type { FileUploadResponse } from "@/hooks/api/file-upload"
import type { Response } from "@/types"
import type { FetchFnParam } from "@/hooks/useFetch"
import {
  filterSupportedFiles,
  isFileTooLarge,
  MAX_FILE_SIZE_MB,
} from "@/lib/fileUtils"

import type { CopilotFile } from "./types"

/**
 * Convert FileUploadResponse to CopilotFile used by Copilot UI components.
 */
export const fileUploadRespToCopilotFile = (
  resp: FileUploadResponse
): CopilotFile => {
  return {
    // Use backend-provided UUID as stable, unique identifier to avoid React key collisions
    id: resp.uuid || `${resp.original_name || "file"}-${Date.now()}`,
    name: resp.original_name || "",
    size: resp.size || 0,
    type: resp.content_type || "",
    uuid: resp.uuid || "",
    fileUrl: resp.file_url,
  }
}

interface ProcessAndUploadOptions {
  files: File[]
  t: TFunction
  alertToast: (props: ToastProps) => unknown
  upload: (
    params?: FetchFnParam<Response<FileUploadResponse>, FormData>
  ) => Promise<Response<FileUploadResponse> | undefined>
  onAdd: (file: CopilotFile) => void
}

/**
 * Shared routine to validate, notify unsupported, and upload Copilot files sequentially.
 * Consumers are responsible for handling API-level errors via their own effects.
 */
export const processAndUploadCopilotFiles = async ({
  files,
  t,
  alertToast,
  upload,
  onAdd,
}: ProcessAndUploadOptions): Promise<void> => {
  if (!files || files.length === 0) return

  // Check for oversized files first
  const oversizedFiles = files.filter((f) => isFileTooLarge(f))
  if (oversizedFiles.length > 0) {
    alertToast({
      title: t("common.fileUpload.error.title"),
      description: t("common.fileUpload.error.fileTooLarge", {
        files: oversizedFiles.map((f) => f.name).join(", "),
        limit: MAX_FILE_SIZE_MB,
        defaultValue: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit: {{files}}`,
      }),
    })
  }

  // Filter out oversized files before checking for supported types
  const nonOversizedFiles = files.filter((f) => !isFileTooLarge(f))
  if (nonOversizedFiles.length === 0) return

  const { supportedFiles, unsupportedFiles } = filterSupportedFiles(
    nonOversizedFiles,
    SUPPORTED_ATTACHMENT_MIME_TYPES
  )
  if (unsupportedFiles.length > 0) {
    alertToast({
      title: t("common.fileUpload.error.title"),
      description: t("common.fileUpload.error.unsupportedType", {
        files: unsupportedFiles.map((f) => f.name).join(", "),
        defaultValue: "Unsupported file types: {{files}}",
      }),
    })
  }
  if (supportedFiles.length === 0) return

  for (const file of supportedFiles) {
    const formData = new FormData()
    formData.append("file", file)
    try {
      await upload({
        body: formData,
        callbackFn: (resp) => {
          const data = resp?.data
          if (!data || !data.uuid) return
          const newFile = fileUploadRespToCopilotFile(data)
          onAdd(newFile)
        },
      })
    } catch (e) {
      // Let callers handle error toasts via their existing effects
      console.error("Copilot upload failed:", e)
    }
  }
}
